<template>
  <ModalComponent
    ref="modalRef"
    submitButtonText="Settle items"
    :submit="handleSubmit"
    :submitDisabled="isSubmitting || !isFormValid"
    size="xl"
    @close="handleClose"
  >
    <template #title>Settle Pending Fee Items</template>
    <template #body>
      <form @submit.prevent="handleSubmit">
        <!-- Header info -->
        <div class="card border mb-3">
          <div class="card-body">
            <div class="row">
              <div class="col-6">
                <small class="text-muted">From:</small>
                <p class="mb-0 fw-bold">Soldera OÜ</p>
                <p class="mb-0 small">Akadeemia tn 3, 51003 Tartu</p>
                <p class="mb-0 small">Reg: 16785003</p>
                <p class="mb-0 small">VAT: EE102643242</p>
              </div>
              <div class="col-6">
                <div class="mb-2">
                  <label
                    for="requestedAt"
                    class="form-label small text-muted mb-1"
                  >
                    Invoice Date
                  </label>
                  <DatePicker
                    id="requestedAt"
                    v-model="formData.requestedAt"
                    dateFormat="dd/mm/yy"
                    :manualInput="false"
                    appendTo="body"
                    class="w-100"
                    showIcon
                  />
                </div>
                <div>
                  <label for="dueAt" class="form-label small text-muted mb-1">
                    Due Date
                  </label>
                  <DatePicker
                    id="dueAt"
                    v-model="formData.dueAt"
                    dateFormat="dd/mm/yy"
                    :manualInput="false"
                    appendTo="body"
                    class="w-100"
                    showIcon
                    @date-select="dueDateTouched = true"
                  />
                  <div
                    v-if="isDueDateBeforeInvoiceDate"
                    class="text-danger small mt-1"
                  >
                    Due date cannot be before the invoice date.
                  </div>
                </div>
                <div class="mt-2">
                  <label
                    for="invoiceLanguage"
                    class="form-label small text-muted mb-1"
                  >
                    Invoice Language
                  </label>
                  <select
                    id="invoiceLanguage"
                    v-model="language"
                    class="form-select"
                  >
                    <option
                      v-for="option in INVOICE_LANGUAGE_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Receiver -->
        <h6 class="mb-3">Receiver Details</h6>

        <div v-if="recipientCandidates.length > 0" class="mb-3">
          <label for="recipient" class="form-label">Invoice to</label>
          <select id="recipient" v-model="recipientId" class="form-select">
            <option :value="null">(this company)</option>
            <option
              v-for="candidate in recipientCandidates"
              :key="candidate.id"
              :value="candidate.id"
            >
              {{ candidate.name }} ({{ candidate.country ?? "—" }})
            </option>
          </select>
          <div v-if="recipient" class="form-text" data-test="recipient-info">
            Selected fee items will move to <em>{{ recipient.name }}</em> and be
            invoiced there. They leave this company's pending list.
          </div>
        </div>

        <div class="mb-3">
          <label for="reference" class="form-label">Reference (Optional)</label>
          <input
            id="reference"
            type="text"
            v-model="formData.reference"
            class="form-control"
            placeholder="e.g., Contract-2024-001"
            maxlength="128"
          />
          <div class="form-text">Optional reference number for the invoice</div>
        </div>

        <div class="mb-3">
          <label for="paymentHeader" class="form-label">
            Payment Header (Address)
          </label>
          <textarea
            id="paymentHeader"
            v-model="formData.paymentHeader"
            class="form-control"
            :class="{ 'is-invalid': isPaymentHeaderTooLong }"
            rows="3"
            :maxlength="PAYMENT_HEADER_MAX_LENGTH"
            placeholder="Company name&#10;Address&#10;Registry code"
          ></textarea>
          <div v-if="isPaymentHeaderTooLong" class="invalid-feedback">
            Payment header must be {{ PAYMENT_HEADER_MAX_LENGTH }} characters or
            fewer (currently {{ formData.paymentHeader.trim().length }}).
          </div>
          <div class="form-text">
            Multi-line receiver block printed on the invoice
          </div>
        </div>

        <div class="mb-3">
          <label for="vatNumber" class="form-label">VAT Number</label>
          <input
            id="vatNumber"
            type="text"
            v-model="formData.vatNumber"
            class="form-control"
            :readonly="recipient !== null"
            maxlength="128"
          />
        </div>

        <hr class="my-4" />

        <!-- Items preview -->
        <SettlementItemsMerger
          :key="openCount"
          v-model:rowGroups="rowGroups"
          :items="previewItems"
          :language
        />

        <div class="alert alert-info" role="alert">
          <strong>Total (incl. VAT):</strong>
          {{ formatMoney(grandTotal) }} €
        </div>
      </form>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import { addDays, isBefore, startOfDay } from "date-fns";
import { find, keyBy } from "lodash-es";
import DatePicker from "primevue/datepicker";
import { computed, nextTick, reactive, ref, useTemplateRef, watch } from "vue";

import ModalComponent from "@/components/common/ModalComponent.vue";
import {
  formatDateTimeIntoIsoDate,
  formatMoney,
} from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { PAYMENT_HEADER_MAX_LENGTH } from "@/money/invoice/invoiceLimits";
import type { InvoiceItem } from "@/types";

import {
  groupDescription,
  groupDescriptionError,
  INVOICE_LANGUAGE_OPTIONS,
  type InvoiceLanguage,
  type RowGroupDraft,
} from "./rowGroups";
import SettlementItemsMerger from "./SettlementItemsMerger.vue";
import type { CompanyDefaults, PreviewItem, RecipientCandidate } from "./types";
import { toPreviewItem } from "./utils";

const props = withDefaults(
  defineProps<{
    show: boolean;
    companyId: number;
    items: PreviewItem[];
    companyDefaults: CompanyDefaults | null;
    recipientCandidates?: RecipientCandidate[];
  }>(),
  { recipientCandidates: () => [] }
);

const emit = defineEmits<{
  close: [];
  settled: [];
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const DUE_DATE_OFFSET_DAYS = 10;

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !isNaN(value.getTime());

const isSubmitting = ref(false);
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const isModalOpen = ref(false);
const dueDateTouched = ref(false);
const language = ref<InvoiceLanguage>("en");
const rowGroups = ref<RowGroupDraft[]>([]);
// Bumped on every open so the items section starts with no checked rows or filters.
const openCount = ref(0);
// null = invoice the company itself.
const recipientId = ref<number | null>(null);
const recipientItems = ref<PreviewItem[]>([]);
// The recipient and invoice day `recipientItems` were fetched for; null when none are loaded.
const loadedRecipientItemsKey = ref<string | null>(null);
const isLoadingRecipientItems = ref(false);
let recipientRequestId = 0;

const formData = reactive<{
  reference: string;
  paymentHeader: string;
  vatNumber: string;
  requestedAt: Date;
  dueAt: Date;
}>({
  reference: "",
  paymentHeader: "",
  vatNumber: "",
  requestedAt: new Date(),
  dueAt: addDays(new Date(), DUE_DATE_OFFSET_DAYS),
});

const recipient = computed(
  () =>
    find(
      props.recipientCandidates,
      (candidate) => candidate.id === recipientId.value
    ) ?? null
);
const invoiceDay = computed(() =>
  isValidDate(formData.requestedAt)
    ? formatDateTimeIntoIsoDate(formData.requestedAt)
    : null
);
const recipientItemsById = computed(() => keyBy(recipientItems.value, "id"));
// The backend restamps the rows for the recipient: its VAT rate, rounded the way its settlement will.
// Overlaid on the selected items so every id (e.g. in merged row groups) resolves while a fetch is in flight or has failed.
const previewItems = computed(() =>
  recipient.value
    ? props.items.map((item) => recipientItemsById.value[item.id] ?? item)
    : props.items
);

const grandTotal = computed(() =>
  previewItems.value.reduce((sum, item) => sum + item.total_with_vat, 0)
);

// Compare by calendar day so a due date on the same day as the invoice date is allowed.
const isDueDateBeforeInvoiceDate = computed(
  () =>
    isValidDate(formData.dueAt) &&
    isValidDate(formData.requestedAt) &&
    isBefore(startOfDay(formData.dueAt), startOfDay(formData.requestedAt))
);

// The textarea's maxlength only limits typing; a prefilled company default or a pasted value can still exceed it.
const isPaymentHeaderTooLong = computed(
  () => formData.paymentHeader.trim().length > PAYMENT_HEADER_MAX_LENGTH
);

const itemsById = computed(() => keyBy(previewItems.value, "id"));
const groupMembers = (group: RowGroupDraft) =>
  group.itemIds.map((id) => itemsById.value[id]);
const groupDescriptions = computed(() =>
  rowGroups.value.map((group) =>
    groupDescription(group, groupMembers(group), language.value).trim()
  )
);
const areRowGroupsValid = computed(() =>
  groupDescriptions.value.every(
    (description) => groupDescriptionError(description) === null
  )
);

// A rebill submits only on rows fetched for the selected recipient and invoice day, so the preview
// matches what settlement posts even after a failed or superseded fetch.
const areRecipientItemsCurrent = computed(
  () =>
    recipientId.value === null ||
    (invoiceDay.value !== null &&
      !isLoadingRecipientItems.value &&
      loadedRecipientItemsKey.value ===
        recipientItemsKey(recipientId.value, invoiceDay.value))
);

const isFormValid = computed(() => {
  return (
    previewItems.value.length > 0 &&
    areRecipientItemsCurrent.value &&
    formData.paymentHeader.trim().length > 0 &&
    !isPaymentHeaderTooLong.value &&
    areRowGroupsValid.value &&
    isValidDate(formData.requestedAt) &&
    isValidDate(formData.dueAt) &&
    !isDueDateBeforeInvoiceDate.value
  );
});

// Keep the due date in sync with the invoice date until the admin picks one manually.
watch(
  () => formData.requestedAt,
  (newRequestedAt) => {
    if (!dueDateTouched.value && isValidDate(newRequestedAt)) {
      formData.dueAt = addDays(newRequestedAt, DUE_DATE_OFFSET_DAYS);
    }
  }
);

// Arrow, not a bare reference: applyReceiverDefaults is declared below (temporal dead zone).
watch(recipientId, () => applyReceiverDefaults());

// VAT is stamped from the recipient and the invoice day, so either change re-reads the charges.
watch(
  () => [recipientId.value, invoiceDay.value] as const,
  ([selectedRecipientId, requestedAt]) => {
    if (selectedRecipientId === null) {
      cancelRecipientItemsLoad();
    } else if (requestedAt !== null) {
      loadRecipientItems(selectedRecipientId, requestedAt);
    }
  }
);

const applyReceiverDefaults = () => {
  const defaults = recipient.value ?? props.companyDefaults;
  formData.paymentHeader = defaults?.payment_header ?? "";
  formData.vatNumber = defaults?.vat ?? "";
  language.value = defaults?.invoice_language ?? "en";
};

const recipientItemsKey = (selectedRecipientId: number, requestedAt: string) =>
  `${selectedRecipientId}|${requestedAt}`;

// Drops the fetch in flight, so its late result or error is ignored.
const cancelRecipientItemsLoad = () => {
  recipientRequestId++;
  isLoadingRecipientItems.value = false;
};

const loadRecipientItems = async (
  selectedRecipientId: number,
  requestedAt: string
) => {
  const requestId = ++recipientRequestId;
  isLoadingRecipientItems.value = true;
  try {
    const response = await goSolidApi.get(
      `/api/admin/company/${props.companyId}/invoicing/pending-fee-items`,
      {
        params: {
          recipient_company_id: selectedRecipientId,
          requested_at: requestedAt,
        },
      }
    );
    if (requestId !== recipientRequestId) return;
    const selectedIds = new Set(props.items.map((item) => item.id));
    recipientItems.value = (response.data.invoice_items as InvoiceItem[])
      .filter((item) => selectedIds.has(item.id))
      .map(toPreviewItem);
    loadedRecipientItemsKey.value = recipientItemsKey(
      selectedRecipientId,
      requestedAt
    );
  } catch (error) {
    if (requestId !== recipientRequestId) return;
    loadedRecipientItemsKey.value = null;
    globalErrorHandler(error);
  } finally {
    if (requestId === recipientRequestId) isLoadingRecipientItems.value = false;
  }
};

const populateFormData = () => {
  recipientId.value = null;
  cancelRecipientItemsLoad();
  recipientItems.value = [];
  loadedRecipientItemsKey.value = null;
  formData.reference = "";
  applyReceiverDefaults();
  formData.requestedAt = new Date();
  formData.dueAt = addDays(formData.requestedAt, DUE_DATE_OFFSET_DAYS);
  dueDateTouched.value = false;
  rowGroups.value = [];
  openCount.value++;
};

watch(
  () => props.show,
  async (shouldShow) => {
    await nextTick();
    if (modalRef.value && isModalOpen.value !== shouldShow) {
      if (shouldShow) {
        populateFormData();
      }
      modalRef.value.toggleModal();
      isModalOpen.value = shouldShow;
    }
  }
);

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;
  try {
    const response = await goSolidApi.post("/api/admin/settle-invoice-items", {
      company_id: props.companyId,
      invoice_item_ids: props.items.map((item) => item.id),
      reference: formData.reference.trim() || null,
      payment_header: formData.paymentHeader.trim() || null,
      vat_number: formData.vatNumber.trim() || null,
      requested_at: formatDateTimeIntoIsoDate(formData.requestedAt),
      due_at: formatDateTimeIntoIsoDate(formData.dueAt),
      language: language.value,
      row_groups: rowGroups.value.map((group, index) => ({
        description: groupDescriptions.value[index],
        invoice_item_ids: group.itemIds,
      })),
      ...(recipient.value ? { recipient_company_id: recipient.value.id } : {}),
    });
    if (response.status === 200) {
      globalSuccessHandler("Success", "Items settled successfully");
      emit("settled");
      handleClose();
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  if (!isSubmitting.value) {
    isModalOpen.value = false;
    emit("close");
  }
};
</script>

<style scoped lang="scss"></style>
